import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { AviaPassengerFareFragment, FareFamilyFragment, InfoMessagesFragment } from '@websky/graphql';
import { SelectedFare } from '../../../../DesktopFlight/types';
import { useTheme } from '../../../../theme';
import { SimpleFlightPrice } from '../SimpleFlightPrice/SimpleFlightPrice';
import Money from '../../../../Money/components/Money';
import PriceInMiles from '../../../../PriceInMiles/PriceInMiles';
import { TaxesTooltip } from '@entities/fare/ui';
import { buildFareBreakdown } from '@entities/fare';
import { CdnIcon } from '@shared/ui';

interface Props {
	fareFamily?: FareFamilyFragment;
	selectedFare: SelectedFare;
	passengerFares?: AviaPassengerFareFragment[];
	canceled?: boolean;
	hasCanceledSegment?: boolean;
	priceInMiles?: number;
	hideOnlyPrice?: boolean;
	onClear?: () => void;
	subsidized?: boolean;
	isOrderLoading?: boolean;
	flightMessages?: InfoMessagesFragment[];
}

export const SelectedFlightPrice: React.FC<Props> = ({
	fareFamily,
	selectedFare,
	passengerFares,
	canceled: isOrderCanceled,
	hasCanceledSegment,
	onClear,
	hideOnlyPrice,
	subsidized,
	isOrderLoading,
	flightMessages = []
}) => {
	const { t } = useTranslation('DesktopFlightInfo');
	const { FlightPriceStyles: css } = useTheme('DesktopFlightRoute');

	const priceInMiles = useMemo(() => {
		if (selectedFare.prices?.[0]?.priceInMiles) {
			return selectedFare.prices[0].priceInMiles;
		}

		return null;
	}, [selectedFare]);

	const hasPrice = Boolean(selectedFare.prices?.[0]?.price || priceInMiles);
	const isCanceled = isOrderCanceled || hasCanceledSegment;

	const breakdown = useMemo(
		() => buildFareBreakdown({ passengerFares: passengerFares ?? [], total: selectedFare.prices?.[0]?.price }),
		[passengerFares, selectedFare]
	);

	return (
		<SimpleFlightPrice
			singleFlight={false}
			best={false}
			selected={true}
			flightMessages={flightMessages}
			subsidized={subsidized}
			originalPriceOrLabel={fareFamily && <div className={css.fare_family}>{fareFamily.title}</div>}
			additionalTaxes={
				priceInMiles && (
					<div className={css.miles__taxes}>
						{t('taxes and fees')}: <Money money={selectedFare.prices[0].price} />
					</div>
				)
			}
			price={
				!hideOnlyPrice && (
					<>
						{!isOrderCanceled && (
							<>
								{hasPrice && (
									<div className={cn(css.prices__wrp)}>
										{!priceInMiles ? (
											<Money
												moneyClassName={css.price__money}
												money={selectedFare.prices[0].price}
												isLoading={isOrderLoading}
											/>
										) : (
											<PriceInMiles
												amount={priceInMiles}
												moneyClassName={cn(css.price__money, css.price_miles)}
												currencyClassName={css.currency}
											/>
										)}

										{breakdown.groups.length > 0 && <TaxesTooltip breakdown={breakdown} />}
									</div>
								)}

								{onClear && (
									<div className={css.clear} onClick={onClear}>
										<CdnIcon icon="Action/Close-Clear" />
									</div>
								)}
							</>
						)}

						{isCanceled && <div className={css.canceled}>{t('Canceled')}</div>}
					</>
				)
			}
		/>
	);
};
